import { ArrowLeft, TrendingUp, Package, Calendar, AlertTriangle, Info, Zap } from 'lucide-react';
import { useForecast } from '../hooks/useData';
import { formatCurrency, getStatusColor } from '../utils/api';

function MetricCard({ label, value, subvalue, icon: Icon, color = 'gray' }) {
  const colors = {
    gray: 'bg-gray-50 text-gray-600',
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon size={20} />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
          {subvalue && <p className="text-xs text-gray-400">{subvalue}</p>}
        </div>
      </div>
    </div>
  );
}

function SimpleChart({ historical, forecast, reorderPoint }) {
  // Simple visualization without recharts
  const allData = [
    ...(historical || []).map(h => ({ ...h, type: 'historical', value: h.y })),
    ...(forecast || []).map(f => ({ ...f, type: 'forecast', value: f.forecast })),
  ];

  if (allData.length === 0) {
    return <p className="text-gray-500 text-center py-8">No data available</p>;
  }

  const maxValue = Math.max(
    ...allData.map(d => d.value),
    ...(forecast || []).map(f => f.upper_bound || f.forecast),
    reorderPoint || 0
  );
  const minValue = 0;
  const range = maxValue - minValue || 1;

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-gray-600">Historical</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-purple-500"></div>
          <span className="text-gray-600">Forecast</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5 bg-red-500"></div>
          <span className="text-gray-600">Reorder Point ({reorderPoint})</span>
        </div>
      </div>

      {/* Chart Area */}
      <div className="relative h-64 border border-gray-200 rounded-lg p-4 bg-gray-50">
        {/* Reorder line */}
        {reorderPoint > 0 && (
          <div
            className="absolute left-0 right-0 border-t-2 border-dashed border-red-400"
            style={{ top: `${100 - ((reorderPoint - minValue) / range) * 100}%` }}
          >
            <span className="absolute right-2 -top-3 text-xs text-red-500 bg-white px-1">
              Reorder: {reorderPoint}
            </span>
          </div>
        )}

        {/* Data bars */}
        <div className="flex items-end h-full gap-1">
          {allData.slice(-20).map((d, i) => {
            const height = ((d.value - minValue) / range) * 100;
            const isPromo = d.promo_factor;
            return (
              <div
                key={i}
                className="flex-1 flex flex-col items-center justify-end"
                title={`${d.ds || d.date}: ${d.value}`}
              >
                <div
                  className={`w-full rounded-t transition-all ${
                    d.type === 'historical'
                      ? 'bg-blue-400'
                      : isPromo
                      ? 'bg-orange-400'
                      : 'bg-purple-400'
                  }`}
                  style={{ height: `${Math.max(height, 2)}%` }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Weekly Breakdown */}
      <div className="text-xs text-gray-500 flex justify-between">
        <span>Historical</span>
        <span>Forecast (8 weeks)</span>
      </div>
    </div>
  );
}

export default function ForecastDetail({ sku, onBack }) {
  const { data, isLoading, error } = useForecast(sku);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft size={20} />
          Back to Inventory
        </button>
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          Error loading forecast: {error?.message || 'SKU not found'}
        </div>
      </div>
    );
  }

  const { forecast, reorder } = data;
  const summary = forecast?.summary || {};
  const promoWeeks = forecast?.promo_impact || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{sku}</h1>
          <p className="text-gray-500">{reorder?.product_name}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
            {forecast?.method === 'prophet' ? 'Prophet' : 'Seasonal MA'}
          </span>
          <span className={getStatusColor(reorder?.status)}>
            {reorder?.status?.replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* Method Badge */}
      {forecast?.method === 'prophet' && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-3">
          <Zap className="text-purple-600" size={20} />
          <div>
            <p className="font-medium text-purple-900">Facebook Prophet Forecast</p>
            <p className="text-sm text-purple-600">
              Using advanced ML with seasonality detection, holiday effects, and promo integration
            </p>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Current Stock"
          value={reorder?.current_stock}
          subvalue={`${reorder?.days_of_stock} days left`}
          icon={Package}
          color={reorder?.status === 'CRITICAL' ? 'red' : 'blue'}
        />
        <MetricCard
          label="Weekly Demand"
          value={Math.round(reorder?.avg_weekly_demand || 0)}
          subvalue={`${reorder?.avg_daily_demand}/day`}
          icon={TrendingUp}
          color="green"
        />
        <MetricCard
          label="Reorder Point"
          value={reorder?.reorder_point}
          subvalue={`Safety: ${reorder?.safety_stock} (CI-based)`}
          icon={AlertTriangle}
          color="yellow"
        />
        <MetricCard
          label="8-Week Forecast"
          value={summary.total_forecast}
          subvalue={`Range: ${summary.forecast_range}`}
          icon={Calendar}
          color="purple"
        />
      </div>

      {/* Confidence Interval Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-start gap-3">
          <Info className="text-blue-600 mt-0.5" size={20} />
          <div className="flex-1">
            <h3 className="font-medium text-gray-900">
              Confidence Interval: {Math.round((forecast?.confidence_interval || 0.8) * 100)}%
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              The forecast range shows where actual demand is expected to fall{' '}
              {Math.round((forecast?.confidence_interval || 0.8) * 100)}% of the time.
              Safety stock is calculated from the upper bound to protect against demand variability.
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Forecast Range</p>
            <p className="text-lg font-bold text-purple-700">
              {summary.total_lower_bound} - {summary.total_upper_bound}
            </p>
          </div>
        </div>
      </div>

      {/* Main Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Demand History & Forecast</h2>
        <SimpleChart
          historical={forecast?.historical}
          forecast={forecast?.forecast}
          reorderPoint={reorder?.reorder_point}
        />
      </div>

      {/* Promo Impact Section */}
      {promoWeeks.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-orange-200 bg-orange-50 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="text-orange-600" size={20} />
            <h3 className="font-semibold text-gray-900">Promo Impact Detected</h3>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            The forecast includes uplift adjustments for the following promotional periods:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {promoWeeks.map((promo, i) => (
              <div key={i} className="bg-white rounded-lg p-3 border border-orange-200">
                <p className="text-sm font-medium text-gray-900">
                  {new Date(promo.date).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short'
                  })}
                </p>
                <p className="text-lg font-bold text-orange-600">
                  +{Math.round((promo.uplift_factor - 1) * 100)}%
                </p>
                <p className="text-xs text-gray-500">{promo.promo_type || 'Promo'}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reorder Recommendation */}
      {reorder?.suggested_order_qty > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6 bg-gradient-to-r from-blue-50 to-cyan-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Reorder Recommendation</h3>
              <p className="text-gray-600 mt-1">
                Order <span className="font-bold text-blue-700">{reorder.suggested_order_qty} units</span> to maintain optimal stock levels
              </p>
              <div className="flex gap-6 mt-3 text-sm text-gray-500">
                <span>Lead Time: {reorder.lead_time_days} days</span>
                <span>MOQ: {reorder.moq} units</span>
                <span>Vendor: {reorder.vendor}</span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Order Value</p>
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(reorder.order_value)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Weekly Forecast Breakdown */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Weekly Forecast Breakdown</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Week</th>
                <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase">Forecast</th>
                <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase">Lower (10%)</th>
                <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase">Upper (90%)</th>
                <th className="text-center py-2 px-3 text-xs font-medium text-gray-500 uppercase">Promo</th>
              </tr>
            </thead>
            <tbody>
              {forecast?.forecast?.map((week, i) => (
                <tr key={i} className={`border-b border-gray-50 ${week.promo_factor ? 'bg-orange-50' : ''}`}>
                  <td className="py-3 px-3">
                    <p className="font-medium text-gray-900">Week {i + 1}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(week.date).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short'
                      })}
                    </p>
                  </td>
                  <td className="py-3 px-3 text-right">
                    <span className="text-lg font-bold text-purple-700">{week.forecast}</span>
                  </td>
                  <td className="py-3 px-3 text-right text-gray-500">{week.lower_bound}</td>
                  <td className="py-3 px-3 text-right text-gray-500">{week.upper_bound}</td>
                  <td className="py-3 px-3 text-center">
                    {week.promo_factor ? (
                      <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
                        +{Math.round((week.promo_factor - 1) * 100)}%
                      </span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-medium">
                <td className="py-3 px-3">Total</td>
                <td className="py-3 px-3 text-right text-purple-700">{summary.total_forecast}</td>
                <td className="py-3 px-3 text-right text-gray-600">{summary.total_lower_bound}</td>
                <td className="py-3 px-3 text-right text-gray-600">{summary.total_upper_bound}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Model Metrics (if available) */}
      {forecast?.metrics && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Forecast Quality Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {forecast.metrics.mape !== undefined && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">MAPE</p>
                <p className="text-xl font-bold text-gray-900">{forecast.metrics.mape}%</p>
                <p className="text-xs text-gray-400">Mean Absolute % Error</p>
              </div>
            )}
            {forecast.metrics.mae !== undefined && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">MAE</p>
                <p className="text-xl font-bold text-gray-900">{forecast.metrics.mae}</p>
                <p className="text-xs text-gray-400">Mean Absolute Error</p>
              </div>
            )}
            {forecast.metrics.rmse !== undefined && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">RMSE</p>
                <p className="text-xl font-bold text-gray-900">{forecast.metrics.rmse}</p>
                <p className="text-xs text-gray-400">Root Mean Square Error</p>
              </div>
            )}
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Data Points</p>
              <p className="text-xl font-bold text-gray-900">{forecast.metrics.data_points}</p>
              <p className="text-xs text-gray-400">Historical weeks</p>
            </div>
          </div>
        </div>
      )}

      {/* Product Details */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Details</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Brand</p>
            <p className="font-medium text-gray-900">{reorder?.brand}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Category</p>
            <p className="font-medium text-gray-900">{reorder?.category}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">MRP</p>
            <p className="font-medium text-gray-900">{formatCurrency(reorder?.mrp)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Cost</p>
            <p className="font-medium text-gray-900">{formatCurrency(reorder?.cost)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
